import type { ReactNode } from "react";
import { ArrowLeft, CheckCircle2 } from "lucide-react";

export type CertificationStandardHeroStat = {
  icon: typeof CheckCircle2;
  label: string;
  value: string | number;
  loading?: boolean;
};

type Props = {
  title: string;
  description: string;
  secondaryDescription: string;
  stats: CertificationStandardHeroStat[];
  onBack: () => void;
  actions?: ReactNode;
  backLabel?: string;
};

export function CertificationStandardHero({
  title,
  description,
  secondaryDescription,
  stats,
  onBack,
  actions,
  backLabel = "Volver a certificaciones",
}: Props) {
  return (
    <section className="calidad-hero dashboard-fade-up">
      <div className="calidad-standard-hero-topline relative z-10">
        <div className="calidad-hero-badge">
          <CheckCircle2 size={14} />
          Certificaciones
        </div>

        <div className="calidad-standard-hero-actions">
          {actions}
          <button
            type="button"
            className="certificaciones-back-button calidad-standard-back"
            onClick={onBack}
          >
            <ArrowLeft size={16} />
            {backLabel}
          </button>
        </div>
      </div>

      <div className="calidad-hero-orb" />
      <div className="calidad-hero-orb-secondary" />

      <div className="relative z-10 flex flex-col gap-7 xl:flex-row xl:items-end xl:justify-between">
        <div className="max-w-4xl">
          <h1 className="text-4xl font-black tracking-[-0.06em] text-slate-950 lg:text-5xl">
            {title}
          </h1>

          <p className="mt-4 max-w-3xl text-[15px] font-medium leading-7 text-slate-500">
            {description}
          </p>

          <p className="mt-3 max-w-3xl text-sm font-semibold leading-6 text-slate-500">
            {secondaryDescription}
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-3 xl:min-w-[560px]">
          {stats.map((stat) => {
            const Icon = stat.icon;

            return (
              <div key={stat.label} className="calidad-hero-stat">
                <div className="flex items-center gap-3">
                  <span className="calidad-hero-stat-icon">
                    <Icon size={18} />
                  </span>
                  <div>
                    <div className="text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                      {stat.label}
                    </div>
                    <div className="mt-1 text-2xl font-black text-slate-950">
                      {stat.loading ? "..." : stat.value}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
